import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import PageHero from "../components/PageHero";

const LANGUAGES = ["Hindi", "Tamil", "Telugu", "Bengali", "Odia", "Punjabi", "Gujarati", "Kannada", "Malayalam", "Other"];

const EMPTY = {
  name: "", mobile: "", language: "", referredBy: "",
  socialLink: "", followers: "", gender: "Female",
};

const WEB3FORMS_ACCESS_KEY = "73ee46f9-bbb7-4ca0-8c37-f0456ddb28ac";

export default function CelebConnectForm() {
  const [form, setForm] = useState(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const update = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const API_BASE = process.env.REACT_APP_API_URL || "https://api.instafrnd.com";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim()) return setError("Please enter your name.");
    if (!/^\d{10}$/.test(form.mobile.trim())) return setError("Please enter a valid 10-digit mobile number.");
    if (!form.language) return setError("Please select your language.");
    if (!form.socialLink.trim()) return setError("Please add your YouTube / Instagram link.");

    setSending(true);
    try {
      // Primary: internal API — stores submission in admin panel DB
      const res = await fetch(`${API_BASE}/api/public/celeb-connect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          mobile: form.mobile,
          language: form.language,
          gender: form.gender,
          social_link: form.socialLink,
          followers: form.followers || undefined,
          referred_by: form.referredBy || undefined,
          kind: "celeb",
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setSubmitted(true);
        setForm(EMPTY);
        return;
      }
      throw new Error(data.message || "Server error");
    } catch (_primaryErr) {
      // Fallback: web3forms so no submission is ever silently lost
      try {
        const fallback = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            access_key: WEB3FORMS_ACCESS_KEY,
            subject: "New Celeb Connect Application — Instafrnd",
            from_name: "Instafrnd Celeb Connect",
            name: form.name,
            mobile: form.mobile,
            language: form.language,
            referred_by: form.referredBy,
            social_link: form.socialLink,
            followers: form.followers,
            gender: form.gender,
          }),
        });
        const fbData = await fallback.json();
        if (fbData.success) {
          setSubmitted(true);
          setForm(EMPTY);
          return;
        }
      } catch (_) { /* ignore fallback error */ }
      setError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Celeb Connect Form — Instafrnd</title>
        <meta name="description" content="Apply to join the Instafrnd Celeb community. Fill in your details and our team will be in touch." />
        <link rel="canonical" href="https://instafrnd.com/celeb-connect" />
        <meta property="og:url" content="https://instafrnd.com/celeb-connect" />
        <meta property="og:title" content="Celeb Connect Form — Instafrnd" />
        <meta property="og:description" content="Apply to join the Instafrnd Celeb community. Fill in your details and our team will be in touch." />
      </Helmet>
      <PageHero
        eyebrow="Celeb Connect Form"
        title="Join our community of 10,000+ creators."
        subtitle="Fill in the form below and get discovered by millions. Our team will contact you within 24 hours."
      />

      <section className="section-pad">
        <div className="wrap">
          <form className="form-card reveal" onSubmit={onSubmit} noValidate>
            <div className="form-row">
              <div className="field">
                <label htmlFor="name">Name *</label>
                <input id="name" name="name" value={form.name} onChange={update} placeholder="Your full name" />
              </div>
              <div className="field">
                <label htmlFor="mobile">Mobile Number *</label>
                <input id="mobile" name="mobile" value={form.mobile} onChange={update} maxLength={10} inputMode="numeric" placeholder="10-digit number" />
              </div>
            </div>

            <div className="form-row">
              <div className="field">
                <label htmlFor="language">Select your language *</label>
                <select id="language" name="language" value={form.language} onChange={update}>
                  <option value="" disabled>Choose a language</option>
                  {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="referredBy">How did you hear about us?</label>
                <input id="referredBy" name="referredBy" value={form.referredBy} onChange={update} placeholder="Instagram, friend, etc." />
              </div>
            </div>

            <div className="form-row">
              <div className="field">
                <label htmlFor="socialLink">YouTube / Instagram Link *</label>
                <input id="socialLink" name="socialLink" value={form.socialLink} onChange={update} placeholder="https://instagram.com/yourhandle" />
              </div>
              <div className="field">
                <label htmlFor="followers">Follower / Subscriber Count</label>
                <input id="followers" name="followers" value={form.followers} onChange={update} inputMode="numeric" placeholder="e.g. 5000" />
              </div>
            </div>

            <div className="field" style={{ marginBottom: 22 }}>
              <label>Gender</label>
              <div className="radio-row">
                {["Male", "Female", "Other"].map((g) => (
                  <label key={g}>
                    <input type="radio" name="gender" value={g} checked={form.gender === g} onChange={update} />
                    {g}
                  </label>
                ))}
              </div>
            </div>

            <button className="btn btn-primary" type="submit" disabled={sending} style={{ width: "100%", justifyContent: "center", padding: "14px", opacity: sending ? 0.7 : 1, cursor: sending ? "not-allowed" : "pointer" }}>
              {sending ? "Submitting…" : "Submit Application"}
            </button>

            {error && <div className="form-note" style={{ color: "#E5484D" }}>{error}</div>}
            {submitted && (
              <div className="form-note ok">
                🎉 Thanks! Your application is in. Our team will reach out within 24 hours.
              </div>
            )}
          </form>
        </div>
      </section>
    </>
  );
}
